import {
  IRIA_PORTAL_STAGE_STEPS,
  LOAN_STAGE_STEPS,
  MEHRSAN_STAGE_STEPS,
  STEP_LOG_ACTION_PREFIX,
  STEP_STATUS,
  STEP_STATUS_LABELS,
  WORKFLOW_STAGE_TITLES,
  buildStepActionTitle,
} from "./loanMehrsanStageConstants";

const pick = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal] ?? null;

export const parseStepLog = (log) => {
  const title = pick(log, "actionTitle", "ActionTitle") || "";
  if (!title.startsWith(`${STEP_LOG_ACTION_PREFIX}|`)) return null;
  const parts = title.split("|");
  if (parts.length !== 3) return null;
  const domain = parts[1];
  const stepId = Number(parts[2]);
  if (!domain || Number.isNaN(stepId)) return null;

  let payload = {};
  const rawDesc = pick(log, "description", "Description");
  if (rawDesc && String(rawDesc).trim().startsWith("{")) {
    try {
      payload = JSON.parse(rawDesc);
    } catch {
      payload = { notes: rawDesc };
    }
  } else if (rawDesc) {
    payload = { notes: rawDesc };
  }

  return {
    domain,
    stepId,
    stageCode: pick(log, "stageCode", "StageCode") || "",
    status: payload.status || STEP_STATUS.PENDING,
    referenceCode:
      payload.referenceCode ||
      pick(log, "referenceNumber", "ReferenceNumber") ||
      pick(log, "trackingCode", "TrackingCode") ||
      "",
    description: payload.description || payload.notes || "",
    attachmentName: payload.attachmentName || "",
    attachmentUrl: pick(log, "attachmentUrl", "AttachmentUrl") || payload.attachmentUrl || "",
    responsibleLabel: payload.responsibleLabel || "",
    actorLabel: payload.actorLabel || "",
    date: pick(log, "createOn", "CreateOn") || payload.date || null,
    logId: pick(log, "id", "Id"),
  };
};

const collectStepLogs = (processes, domain) => {
  const map = new Map();
  (processes || []).forEach((process) => {
    const logs = process?.logs || process?.Logs || [];
    logs.forEach((log) => {
      const parsed = parseStepLog(log);
      if (!parsed || parsed.domain !== domain) return;
      const existing = map.get(parsed.stepId);
      if (!existing) {
        map.set(parsed.stepId, parsed);
        return;
      }
      const existingTime = new Date(existing.date || 0).getTime();
      const nextTime = new Date(parsed.date || 0).getTime();
      if (nextTime >= existingTime) map.set(parsed.stepId, parsed);
    });
  });
  return map;
};

const isStepCompleted = (status) => status === STEP_STATUS.COMPLETED;

const normalizeUiStatus = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  const value = raw.trim();
  if (!value) return null;
  const known = Object.values(STEP_STATUS);
  const match = known.find((s) => s.toLowerCase() === value.toLowerCase());
  return match || value;
};

const resolveSequentialStatus = (stepId, logStatus, completedBefore) => {
  if (logStatus) return logStatus;
  if (completedBefore) return STEP_STATUS.WAITING;
  if (stepId === 1) return STEP_STATUS.PENDING;
  return STEP_STATUS.WAITING;
};

const buildStepRows = (definitions, logMap, labelKey) => {
  let completedBefore = true;
  return definitions.map((def) => {
    const log = logMap.get(def.id);
    const status = resolveSequentialStatus(
      def.id,
      log?.status || def.timelineStatus || null,
      completedBefore
    );
    if (!isStepCompleted(status)) completedBefore = false;

    return {
      id: def.id,
      title: def.title,
      stageCode: def.stageCode || "",
      stagePurposeCode: def.stagePurposeCode || null,
      responsibleLabel:
        log?.[labelKey] ||
        log?.responsibleLabel ||
        log?.actorLabel ||
        def[labelKey] ||
        def.responsibleLabel ||
        def.actorLabel ||
        "—",
      actorLabel: def.actorLabel || log?.actorLabel || "",
      status,
      statusLabel: STEP_STATUS_LABELS[status] || STEP_STATUS_LABELS[STEP_STATUS.PENDING],
      date: log?.date || null,
      referenceCode: log?.referenceCode || "",
      description: log?.description || "",
      attachmentName: log?.attachmentName || "",
      attachmentUrl: log?.attachmentUrl || "",
      logId: log?.logId || null,
    };
  });
};

const pickWorkflowTimeline = (process) =>
  process?.workflowTimeline ?? process?.WorkflowTimeline ?? null;

const pickPartnerCode = (process) =>
  pick(process, "lendingPartnerCode", "LendingPartnerCode") ||
  pick(pickWorkflowTimeline(process), "partnerCode", "PartnerCode") ||
  "";

const resolveIriaPortalStepDefinitions = () =>
  IRIA_PORTAL_STAGE_STEPS.map((step) => ({
    id: step.id,
    title: step.title,
    actorLabel: step.actorLabel || "",
    stageCode: step.stageCode,
    stagePurposeCode: step.id === 2 ? "CREDIT_VALIDATION" : null,
  }));

const pickTimelineStages = (timeline) => {
  const stages = timeline?.stages ?? timeline?.Stages;
  return Array.isArray(stages) ? stages : [];
};

/**
 * Prefer a loan process that already carries WorkflowTimeline.Stages from GetById.
 */
export const resolveLoanWorkflowTimeline = (processes) => {
  for (const process of processes || []) {
    const timeline = pickWorkflowTimeline(process);
    const stages = pickTimelineStages(timeline);
    if (stages.length > 0) {
      return { process, timeline, stages };
    }
  }
  return null;
};

const mapTimelineStagesToDefinitions = (stages) =>
  [...(stages || [])]
    .map((stage, index) => {
      const order = Number(
        stage.order ?? stage.Order ?? stage.stageOrder ?? stage.StageOrder ?? index + 1
      );
      const stageCode = stage.stageCode ?? stage.StageCode ?? "";
      const resolvedTitle = resolveWorkflowStageTitle(stageCode, order, stage.title ?? stage.Title);
      const knownMeta = WORKFLOW_STAGE_TITLES[stageCode];
      const legacyLoan = LOAN_STAGE_STEPS.find((s) => s.id === order);
      const iriaStep =
        /^Iria/i.test(stageCode || "") || /^Iria/i.test(stage.stageCode ?? stage.StageCode ?? "")
          ? IRIA_PORTAL_STAGE_STEPS.find(
              (s) => s.stageCode === stageCode || s.id === order
            )
          : null;
      return {
        id: Number.isFinite(order) && order > 0 ? order : index + 1,
        title: iriaStep?.title || resolvedTitle,
        actorLabel:
          iriaStep?.actorLabel ??
          knownMeta?.actorLabel ??
          legacyLoan?.actorLabel ??
          stage.actorLabel ??
          stage.ActorLabel ??
          "",
        stageCode,
        stagePurposeCode: stage.stagePurposeCode ?? stage.StagePurposeCode ?? null,
        timelineStatus: normalizeUiStatus(
          stage.status ?? stage.Status ?? stage.uiStatus ?? stage.UiStatus
        ),
      };
    })
    .sort((a, b) => a.id - b.id);

/** Repair UTF-8 mojibake (e.g. Latin-1 misread Persian). */
export const repairUtf8Mojibake = (text) => {
  if (!text || typeof text !== "string") return text;
  if (!/[ÙØÛÃ]/.test(text)) return text;

  try {
    const bytes = Uint8Array.from([...text].map((ch) => ch.charCodeAt(0) & 0xff));
    const decoded = new TextDecoder("utf-8").decode(bytes);
    if (decoded && !decoded.includes("\uFFFD") && decoded !== text) return decoded;
  } catch {
    /* fall through */
  }

  try {
    const legacy = decodeURIComponent(escape(text));
    if (legacy && legacy !== text) return legacy;
  } catch {
    /* ignore */
  }

  return text;
};

export const resolveWorkflowStageTitle = (stageCode, order, storedTitle) => {
  if (stageCode && WORKFLOW_STAGE_TITLES[stageCode]?.title) {
    return WORKFLOW_STAGE_TITLES[stageCode].title;
  }

  if (/^LoanStep\d+$/i.test(stageCode || "")) {
    const legacy = LOAN_STAGE_STEPS.find((s) => s.id === order);
    if (legacy?.title) return legacy.title;
  }

  const repaired = repairUtf8Mojibake(storedTitle);
  if (repaired && String(repaired).trim()) return repaired;

  return order > 0 ? `مرحله ${order}` : storedTitle || "—";
};

/**
 * Dynamic loan stage rows: WorkflowTimeline first, else legacy LOAN_STAGE_STEPS.
 * step.id remains StageOrder so writes keep LM_STAGE_STEP|loan|{N}.
 */
export const buildLoanStageSteps = (processes) => {
  const logMap = collectStepLogs(processes, "loan");
  const resolved = resolveLoanWorkflowTimeline(processes);
  if (resolved) {
    const definitions = mapTimelineStagesToDefinitions(resolved.stages);
    return buildStepRows(definitions, logMap, "actorLabel");
  }

  const primary = (processes || [])[0];
  if (pickPartnerCode(primary) === "IRIA_PAY") {
    return buildStepRows(resolveIriaPortalStepDefinitions(), logMap, "actorLabel");
  }

  return buildStepRows(LOAN_STAGE_STEPS, logMap, "actorLabel");
};

/** Total loan stages for progress/overall status (from built steps or legacy constant). */
export const getLoanStageTotalCount = (loanSteps) => {
  if (Array.isArray(loanSteps) && loanSteps.length > 0) return loanSteps.length;
  return LOAN_STAGE_STEPS.length;
};

export const countCompletedSteps = (steps) =>
  (steps || []).filter((s) => s.status === STEP_STATUS.COMPLETED).length;

/** اولین مرحله‌ای که هنوز تکمیل نشده و قابل اقدام است. */
export const getNextActionableStep = (steps) =>
  (steps || []).find(
    (s) =>
      s &&
      s.status !== STEP_STATUS.COMPLETED &&
      s.status !== STEP_STATUS.NOT_REQUIRED
  ) || null;

export const resolveProcessOverallStatus = (completed, total, notRequired = false) => {
  if (notRequired) return { key: "notRequired", label: "عدم نیاز" };
  if (completed <= 0) return { key: "notStarted", label: "شروع نشده" };
  if (completed >= total) return { key: "completed", label: "تکمیل شده" };
  return { key: "inProgress", label: "در حال انجام" };
};

export const buildMehrsanStageSteps = (processes) => {
  const logMap = collectStepLogs(processes, "mehrsan");
  return buildStepRows(MEHRSAN_STAGE_STEPS, logMap, "responsibleLabel");
};

export const buildStepSavePayload = ({
  domain,
  stepId,
  status,
  referenceCode,
  description,
  attachmentUrl,
  attachmentName,
  responsibleLabel,
  actorLabel,
}) => ({
  ActionTitle: buildStepActionTitle(domain, stepId),
  Description: JSON.stringify({
    status,
    referenceCode: referenceCode || "",
    description: description || "",
    attachmentUrl: attachmentUrl || "",
    attachmentName: attachmentName || "",
    responsibleLabel: responsibleLabel || "",
    actorLabel: actorLabel || "",
  }),
  ReferenceNumber: referenceCode || null,
  AttachmentUrl: attachmentUrl || null,
});

export const mapUiStatusToMehrsanApiStatus = (status) => {
  if (status === STEP_STATUS.COMPLETED) return 4;
  if (status === STEP_STATUS.REJECTED) return 3;
  return 1;
};

export const mapUiStatusToLoanApiStatus = (status) => {
  if (status === STEP_STATUS.COMPLETED) return 5;
  if (status === STEP_STATUS.REJECTED) return 6;
  if (status === STEP_STATUS.REVIEWING) return 3;
  if (status === STEP_STATUS.NEEDS_ACTION) return 2;
  return 1;
};
